import { useState, useEffect, useRef } from "react";
import "./Navbar.css";
import logo from "../../assets/logoo.png"
import logoN from "../../assets/logoN.png"
import logoS from "../../assets/logoSS.png"

// ─── Nav structure ────────────────────────────────────────────────────────────
// Top-level labels: exactly as shown in the website screenshot
// About | Thinking | Approach | Impact | Industries | Insights | Careers
// Dropdown children: mapped from the sitemap image
// ─────────────────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  {
    label: "About",
    children: [
      { label: "Vision", href: "/about/vision" },
      { label: "Mission", href: "/about/mission" },
      { label: "Purpose", href: "/about/purpose" },
      { label: "Value Proposition", href: "/about/value-proposition" },
      { label: "Advisory Team", href: "/about/leadership" },
      // { label: "Principal Advisor",  href: "/about/principal-advisor" },
      // { label: "Advisory Team",      href: "/about/advisory-team" },
    ],
  },
  {
    label: "Services",
    children: [
      { label: "AI Strategy & Advisory", href: "/approach/ai-strategy" },
      { label: "Digital Transformation Advisory", href: "/approach/digital-transformation" },
      { label: "Data Strategy Advisory", href: "/approach/data-strategy" },
      { label: "IT Operations Advisory", href: "/approach/it-operations" },
      { label: "GCC Advisory", href: "/approach/gcc-advisory" },
    ],
  },
  {
    label: "Solutions",
    children: [
      { label: "AI Readiness Assessment", href: "/impact/ai-readiness" },
      { label: "Technology Assessment", href: "/impact/technology-assessment" },
      { label: "Operating Model Assessment", href: "/impact/operating-model" },
      { label: "Process Optimization Assessment", href: "/impact/process-optimization" },
      { label: "Digital Transformation Assessment", href: "/impact/dt-roadmap" },
    ],
  },
  {
    label: "Insights",
    children: [
      { label: "Blogs", href: "/thinking/blogs" },
      { label: "Whitepapers", href: "/thinking/whitepapers" },
      { label: "Case Studies", href: "/thinking/case-studies" },
    ],
  },

  {
    label: "Opportunities",
    children: [
      { label: "Advisory Partner", href: "/insights/advisory-partner" },
      { label: "Delivery Partner", href: "/insights/delivery-partner" },
      { label: "Business Partner", href: "/insights/business-partner" },
      { label: "Employee Partner", href: "/insights/employee-partner" },
    ],
  },
  {
    label: "Contact",
    children: [
      // { label: "Contact Form", href: "/contact" },
      { label: "Book a Consultation", href: "/contact" },
      { label: "Global Reach", href: "/global-reach" },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const onEnter = (label) => {
    clearTimeout(closeTimer.current);
    setActiveDropdown(label);
  };

  const onLeave = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 130);
  };

  return (
    <nav
      ref={navRef}
      className={`br-nav${scrolled ? " br-nav--scrolled" : ""}`}
      aria-label="Main navigation"
    >
      <div className="br-nav__inner">

        {/* ── Logo ── */}
        <a href="/" className="br-nav__logo" aria-label="BaiRees Global Advisory">
          <span className="br-nav__logo-mark">
            <img src={logo} alt="" className="br-nav__logo-img" />
          </span >
          <span className="br-nav__logo-text-right">
            <span className="br-nav__logo-text">
              <img className="br-nav__logo-img-text" src={logoN} alt="" />
              {/* <img className="br-nav__logo-img-sub" src={logoS} alt="" /> */}
            </span>
            {/* <span className="br-nav__logo-name">BAIREES</span> */}
            <span className="br-nav__logo-sub">Building Intelligent Organizations™</span>
          </span>
        </a>

        {/* ── Desktop links ── */}
        <ul className="br-nav__links" role="menubar">
          {NAV_ITEMS.map((item) => (
            <li
              key={item.label}
              role="none"
              className={`br-nav__item${activeDropdown === item.label ? " br-nav__item--active" : ""}`}
              onMouseEnter={() => onEnter(item.label)}
              onMouseLeave={onLeave}
            >
              <button
                className="br-nav__link"
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={activeDropdown === item.label}
                onClick={() =>
                  setActiveDropdown(activeDropdown === item.label ? null : item.label)
                }
              >
                {item.label}
                <svg
                  className={`br-nav__chevron${activeDropdown === item.label ? " br-nav__chevron--open" : ""}`}
                  width="10" height="6" viewBox="0 0 10 6" fill="none"
                >
                  <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Dropdown */}
              <div
                className={`br-nav__dropdown${activeDropdown === item.label ? " br-nav__dropdown--visible" : ""}`}
                role="menu"
                aria-label={`${item.label} submenu`}
                onMouseEnter={() => onEnter(item.label)}
                onMouseLeave={onLeave}
              >
                <div className="br-nav__dropdown-inner">
                  <p className="br-nav__dropdown-heading">{item.label}</p>
                  <ul className="br-nav__dropdown-list">
                    {item.children.map((child) => (
                      <li key={child.label} role="none">
                        <a href={child.href} className="br-nav__dropdown-link" role="menuitem">
                          <span className="br-nav__dropdown-dot" aria-hidden="true" />
                          {child.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {/* ── CTA ── */}
        <a href="/contact" className="br-nav__cta">
          Start a Conversation
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor"
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>

        {/* ── Hamburger ── */}
        <button
          className={`br-nav__hamburger${mobileOpen ? " br-nav__hamburger--open" : ""}`}
          onClick={() => { setMobileOpen(!mobileOpen); setMobileExpanded(null); }}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          <span /><span /><span />
        </button>
      </div>

      {/* ── Mobile drawer ── */}
      <div
        className={`br-nav__mobile${mobileOpen ? " br-nav__mobile--open" : ""}`}
        aria-hidden={!mobileOpen}
      >
        <div className="br-nav__mobile-scroll">
          {NAV_ITEMS.map((item) => (
            <div key={item.label} className="br-nav__mobile-section">
              <button
                className="br-nav__mobile-toggle"
                onClick={() =>
                  setMobileExpanded(mobileExpanded === item.label ? null : item.label)
                }
                aria-expanded={mobileExpanded === item.label}
              >
                {item.label}
                <svg
                  className={`br-nav__chevron${mobileExpanded === item.label ? " br-nav__chevron--open" : ""}`}
                  width="10" height="6" viewBox="0 0 10 6" fill="none"
                >
                  <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <div className={`br-nav__mobile-children${mobileExpanded === item.label ? " br-nav__mobile-children--open" : ""}`}>
                <ul>
                  {item.children.map((child) => (
                    <li key={child.label}>
                      <a href={child.href} className="br-nav__mobile-child-link">
                        <span className="br-nav__dropdown-dot" aria-hidden="true" />
                        {child.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}

          <div className="br-nav__mobile-cta-wrap">
            <a href="/contact" className="br-nav__mobile-cta">
              Start a Conversation
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>


        </div>
      </div>
    </nav>
  );
}